// Brand comparison page (/compare). The brand listing, brand and product
// pages are rendered on the server and need nothing from here.
import { initI18n, applyTranslations, t } from '../shared/i18n.ts'
import { escapeHtml } from '../shared/utils.ts'
import { trackBrandView, trackProductView } from '../shared/analytics.ts'
import { localized_compare_matchup_path } from '../shared/routes'
import { api_v1_brands_path, api_v1_brand_path } from '../shared/api-routes'
import type { NozzleSeriesGroup, BrandListItem, BrandDetail } from './brands.types'

export const BRAND_INITIALS = {
  hunter: 'H', rainbird: 'RB', gardena: 'G', toro: 'T'
}

function getLang() {
  const pathMatch = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/)
  return (pathMatch && pathMatch[1]) || navigator.language.slice(0, 2) || 'en'
}

// ── API helpers ───────────────────────────────────────
async function fetchBrands(): Promise<BrandListItem[]> {
  const lang = getLang()
  const res = await fetch(`${api_v1_brands_path()}?lang=${lang}`)
  if (!res.ok) throw new Error('Failed to fetch brands')
  return res.json() as Promise<BrandListItem[]>
}

async function fetchBrand(slug: string): Promise<BrandDetail> {
  const lang = getLang()
  const res = await fetch(`${api_v1_brand_path(slug)}?lang=${lang}`)
  if (!res.ok) throw new Error('Brand not found')
  return res.json() as Promise<BrandDetail>
}

// ── Compare page ─────────────────────────────────────
async function initComparePage() {
  const page = document.querySelector('.compare-page')
  if (!page) return

  const brand1Select = document.getElementById('brand1Select')
  const brand2Select = document.getElementById('brand2Select')
  const resultContainer = document.getElementById('compareResult')
  if (!brand1Select || !brand2Select || !resultContainer) return

  try {
    const brandsCache = await fetchBrands()
    const options = brandsCache.map((b: BrandListItem) =>
      `<option value="${escapeHtml(b.slug)}">${escapeHtml(b.name)}</option>`
    ).join('');

    (brand1Select as HTMLSelectElement).innerHTML = `<option value="">${t('brands.selectBrand')}</option>${options}`;
    (brand2Select as HTMLSelectElement).innerHTML = `<option value="">${t('brands.selectBrand')}</option>${options}`

    // Pre-select from URL matchup (e.g., hunter-vs-rainbird)
    const matchup = (page as HTMLElement).dataset.matchup
    if (matchup) {
      const parts = matchup.split('-vs-')
      if (parts.length === 2) {
        (brand1Select as HTMLSelectElement).value = parts[0];
        (brand2Select as HTMLSelectElement).value = parts[1]
        await runComparison()
      }
    }
  } catch {
    resultContainer.innerHTML = `<p class="brands-loading">${t('brands.loadError')}</p>`
    return
  }

  async function runComparison() {
    // TS loses outer-scope narrowing through async closures, so re-guard
    // here. The outer initComparePage already returned early if any
    // of the three elements is missing.
    if (!resultContainer) return

    const s1 = (brand1Select as HTMLSelectElement).value
    const s2 = (brand2Select as HTMLSelectElement).value
    if (!s1 || !s2 || s1 === s2) {
      resultContainer.innerHTML = ''
      return
    }

    resultContainer.innerHTML = `<div class="brands-loading">${t('brands.loading')}</div>`

    try {
      const [b1, b2] = await Promise.all([fetchBrand(s1), fetchBrand(s2)])
      resultContainer.innerHTML = renderComparison(b1, b2)

      // Update title and URL
      document.title = `${b1.name} vs ${b2.name} — ${t('brands.comparison')} | SmartPluvia`
      const compareTitle = document.getElementById('compareTitle')
      if (compareTitle) compareTitle.textContent = `${b1.name} vs ${b2.name}`

      const newPath = localized_compare_matchup_path(getLang(), `${s1}-vs-${s2}`)
      if (window.location.pathname !== newPath) {
        history.replaceState(null, '', newPath)
      }
    } catch {
      resultContainer.innerHTML = `<p class="brands-loading">${t('brands.compareError')}</p>`
    }
  }

  brand1Select.addEventListener('change', runComparison)
  brand2Select.addEventListener('change', runComparison)
}

function renderComparison(b1: BrandDetail, b2: BrandDetail): string {
  const i1 = escapeHtml((BRAND_INITIALS as Record<string, string>)[b1.slug] || b1.name?.charAt(0) || '')
  const i2 = escapeHtml((BRAND_INITIALS as Record<string, string>)[b2.slug] || b2.name?.charAt(0) || '')

  function highlight(v1: number, v2: number): [string, string] {
    if (v1 > v2) return ['highlight', '']
    if (v2 > v1) return ['', 'highlight']
    return ['', '']
  }

  const [nc1, nc2] = highlight(b1.nozzle_count, b2.nozzle_count)
  const [vc1, vc2] = highlight(b1.valve_count, b2.valve_count)
  const [tc1, tc2] = highlight(b1.total_products, b2.total_products)
  const [sc1, sc2] = highlight(
    (b1.nozzles_by_series || []).length,
    (b2.nozzles_by_series || []).length
  )

  const seriesComparison = renderSeriesComparison(b1, b2)

  return `
    <table class="compare-table">
      <thead>
        <tr>
          <th></th>
          <th class="brand-col"><span class="brand-card-logo brand-card-logo--inline">${i1}</span> ${escapeHtml(b1.name)}</th>
          <th class="brand-col"><span class="brand-card-logo brand-card-logo--inline">${i2}</span> ${escapeHtml(b2.name)}</th>
        </tr>
      </thead>
      <tbody>
        <tr><td>${t('brands.founded')}</td><td>${escapeHtml(String(b1.founded ?? ''))}</td><td>${escapeHtml(String(b2.founded ?? ''))}</td></tr>
        <tr><td>${t('brands.headquarters')}</td><td>${escapeHtml(b1.headquarters || '')}</td><td>${escapeHtml(b2.headquarters || '')}</td></tr>
        <tr><td>${t('brands.country')}</td><td>${escapeHtml(b1.country || '')}</td><td>${escapeHtml(b2.country || '')}</td></tr>
        <tr><td>${t('brands.nozzles')}</td><td class="${nc1}">${b1.nozzle_count}</td><td class="${nc2}">${b2.nozzle_count}</td></tr>
        <tr><td>${t('brands.valves')}</td><td class="${vc1}">${b1.valve_count}</td><td class="${vc2}">${b2.valve_count}</td></tr>
        <tr><td>${t('brands.totalProducts')}</td><td class="${tc1}">${b1.total_products}</td><td class="${tc2}">${b2.total_products}</td></tr>
        <tr><td>${t('brands.series')}</td><td class="${sc1}">${(b1.nozzles_by_series || []).length}</td><td class="${sc2}">${(b2.nozzles_by_series || []).length}</td></tr>
        <tr><td>${t('brands.markets')}</td><td>${escapeHtml((b1.markets || []).join(', '))}</td><td>${escapeHtml((b2.markets || []).join(', '))}</td></tr>
      </tbody>
    </table>

    ${seriesComparison}
  `
}

function renderSeriesComparison(b1: BrandDetail, b2: BrandDetail): string {
  const s1: NozzleSeriesGroup[] = b1.nozzles_by_series || []
  const s2: NozzleSeriesGroup[] = b2.nozzles_by_series || []
  if (s1.length === 0 && s2.length === 0) return ''

  const maxLen = Math.max(s1.length, s2.length)
  let rows = ''
  for (let i = 0; i < maxLen; i++) {
    const left = s1[i]
    const right = s2[i]
    rows += `<tr>
      <td>${left ? `<strong>${escapeHtml(left.series)}</strong> (${t('brands.modelsCount', { count: left.models.length })})` : ''}</td>
      <td>${right ? `<strong>${escapeHtml(right.series)}</strong> (${t('brands.modelsCount', { count: right.models.length })})` : ''}</td>
    </tr>`
  }

  return `
    <div class="compare-series-section">
      <h3>${t('brands.productSeries')}</h3>
      <table class="compare-table">
        <thead>
          <tr>
            <th class="brand-col">${escapeHtml(b1.name)}</th>
            <th class="brand-col">${escapeHtml(b2.name)}</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  `
}

// ── Init ──────────────────────────────────────────────
export async function initBrands() {
  await initI18n()

  const path = window.location.pathname
  // Strip optional locale prefix (e.g., /en/compare → /compare)
  const normalizedPath = path.replace(/^\/[a-z]{2}(\/|$)/, '/')

  // The brand listing, brand and product pages are rendered on the server;
  // only the comparison picker builds anything in the browser. Views of a
  // brand or product page still count toward their Plausible goals.
  const productPage = document.querySelector<HTMLElement>('.product-page')
  const brandPage = document.querySelector<HTMLElement>('.brand-page')
  const productPath = normalizedPath.match(/^\/brands\/([^/]+)\/(?:nozzles|valves)\//)
  if (productPage?.dataset.productSlug && productPath) {
    trackProductView(productPage.dataset.productSlug, productPath[1])
  } else if (brandPage?.dataset.brandSlug) {
    trackBrandView(brandPage.dataset.brandSlug)
  } else if (normalizedPath.startsWith('/compare')) {
    await initComparePage()
  }

  applyTranslations()
}
